import { useState, useEffect, useMemo } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf, isCoachedTrainee } from '../../utils/entitlements'
import { trainingBlockApi, trainingProgramApi } from '../../api'
import type { TrainingTagDto, TrainingTagsGroupedDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES, flattenTrainingTags, PROGRAM_TAG_CATEGORIES } from '../../api/trainingBlockApi'
import type { ApiError } from '../../api/client'
import type { TrainingBlock } from '../../types/api'
import { Button, DashedAddButton, ExperimentalTextImportOption, StatusBadge } from '../ui'
import { TrainingBlockCard } from './TrainingBlockCard'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { TagSelector } from './TagSelector'
import { BlockItemsEditor } from './BlockItemsEditor'
import { emptyItem, itemsToApi, type ItemRow } from './blockItemHelpers'
import type { TrainingBlockItem } from '../../types/api'
import { TagCategoryFilter } from './TagCategoryFilter'
import { captureEvent } from '../../posthog'
import {
  emptyTagCategoryFilterState,
  matchesTagCategoryFilters,
  type TagCategoryFilterState,
} from './tagCategoryFilterUtils'

// ─── Block Selection Page ────────────────────────────────────────────────────

interface SelectionMode {
  type: 'add' | 'replace'
  slotIndex?: number
}

interface BlockSelectionPageProps {
  blocks: TrainingBlock[]
  availableTags: TrainingTagDto[]
  tagEmojiMap: Record<string, string>
  mode: SelectionMode
  currentSlotBlockId?: number
  onConfirm: (blockIds: number[]) => void
  onClose: () => void
  onCreateNew: () => void
}

function BlockSelectionPage({
  blocks,
  availableTags,
  tagEmojiMap,
  mode,
  currentSlotBlockId,
  onConfirm,
  onClose,
  onCreateNew,
}: BlockSelectionPageProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [activeFilters, setActiveFilters] = useState<TagCategoryFilterState>(emptyTagCategoryFilterState())
  const [selected, setSelected] = useState<Set<number>>(
    mode.type === 'replace' && currentSlotBlockId != null
      ? new Set([currentSlotBlockId])
      : new Set()
  )

  const usedTagKeys = useMemo(() => {
    const keys = new Set<string>()
    blocks.forEach(b => b.tags?.forEach(tag => keys.add(tag)))
    return keys
  }, [blocks])

  const tagLabelMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.label])),
    [availableTags]
  )

  const filtered = useMemo(
    () => blocks.filter(b => matchesTagCategoryFilters(b.tags, activeFilters)),
    [blocks, activeFilters],
  )

  function toggleBlock(blockId: number) {
    if (mode.type === 'replace') {
      onConfirm([blockId])
      return
    }
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(blockId)) next.delete(blockId)
      else next.add(blockId)
      return next
    })
  }

  const selectedCount = selected.size

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: theme.bgColor }}
    >
      {/* Header */}
      <div
        className="shrink-0 flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
      >
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-xl -ml-1"
          style={{ color: theme.text.primary }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <p className="text-base font-bold flex-1" style={{ color: theme.text.primary }}>
          {mode.type === 'add'
            ? t.createTrainingProgram.selectBlock
            : t.createTrainingProgram.changeBlock}
        </p>
        {mode.type === 'add' && selectedCount > 0 && (
          <span
            className="text-sm font-semibold px-2.5 py-1 rounded-full"
            style={{
              backgroundColor: `${theme.buttonColor}20`,
              color: theme.accent.text,
            }}
          >
            {selectedCount}
          </span>
        )}
      </div>

      {usedTagKeys.size > 0 && (
        <div className="shrink-0 px-4 py-3" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
          <TagCategoryFilter
            tags={availableTags}
            usedTagKeys={usedTagKeys}
            categories={BLOCK_TAG_CATEGORIES}
            value={activeFilters}
            onChange={setActiveFilters}
            bleed={false}
          />
        </div>
      )}

      {/* Block list — same card layout as TrainingBlocks / program detail */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-5 py-12 flex flex-col items-center gap-4">
            <p className="text-center text-sm" style={{ color: theme.text.muted }}>
              {blocks.length === 0
                ? t.createTrainingProgram.noSavedBlocks
                : t.trainingBlocks.filterEmpty}
            </p>
            <DashedAddButton onClick={onCreateNew}>
              {t.createTrainingProgram.createNewBlock}
            </DashedAddButton>
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-3 pb-32">
            {filtered.map(block => {
              const isSelected = selected.has(block.id)
              const isCurrent = mode.type === 'replace' && block.id === currentSlotBlockId
              return (
                <TrainingBlockCard
                  key={block.id}
                  name={block.name}
                  items={block.items}
                  tags={block.tags}
                  tagEmojiMap={tagEmojiMap}
                  tagLabelMap={tagLabelMap}
                  selected={isSelected}
                  onClick={() => toggleBlock(block.id)}
                  headerTrailing={
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 border-2 transition-all"
                      style={isSelected ? {
                        backgroundColor: theme.buttonColor,
                        borderColor: theme.buttonColor,
                      } : {
                        backgroundColor: 'transparent',
                        borderColor: theme.cardBorder,
                      }}
                    >
                      {isSelected && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                  }
                  banner={isCurrent ? (
                    <div
                      className="px-4 py-2 text-xs font-medium"
                      style={{
                        backgroundColor: `${theme.buttonColor}10`,
                        color: theme.accent.text,
                        borderTop: `1px solid ${theme.buttonColor}25`,
                      }}
                    >
                      {t.createTrainingProgram.currentBlock}
                    </div>
                  ) : undefined}
                />
              )
            })}
          </div>
        )}
      </div>

      {/* Sticky confirm button — only for multi-select (add) mode */}
      {mode.type === 'add' && (
        <div
          className="shrink-0 px-4 py-4"
          style={{
            borderTop: `1px solid ${theme.cardBorder}`,
            backgroundColor: theme.bgColor,
          }}
        >
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            disabled={selectedCount === 0}
            onClick={() => onConfirm(Array.from(selected))}
          >
            {selectedCount === 0
              ? t.createTrainingProgram.selectBlock
              : t.createTrainingProgram.addNBlocks.replace('{{count}}', String(selectedCount))}
          </Button>
        </div>
      )}
    </div>
  )
}

type ProgramSlot =
  | { kind: 'existing'; blockId: number }
  | { kind: 'new'; name: string; items: ItemRow[] }

function itemsFromRows(rows: ItemRow[]): TrainingBlockItem[] {
  return itemsToApi(rows).map((item, i) => ({
    sortOrder: i,
    note: item.note,
    exercises: item.exercises.map((ex, j) => ({ name: ex.name, sortOrder: j })),
  }))
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function CreateTrainingProgram() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = id !== undefined
  const theme = useTheme()
  const { t, translate } = useTranslation()
  const { user } = useAuth()
  const blocked =
    isCoachedTrainee(user) || (!isEdit && !entitlementsOf(user).createOwnBlocksAndPrograms)

  const [availableBlocks, setAvailableBlocks] = useState<TrainingBlock[]>([])
  const [availableTags, setAvailableTags] = useState<TrainingTagDto[]>([])
  const [tagCatalog, setTagCatalog] = useState<TrainingTagsGroupedDto>({
    muscles: [], levels: [], genders: [], types: [],
  })
  const [loadingBlocks, setLoadingBlocks] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [programTags, setProgramTags] = useState<string[]>([])
  const [slots, setSlots] = useState<ProgramSlot[]>([])
  const [selectionMode, setSelectionMode] = useState<SelectionMode | null>(null)
  const [creatingIndex, setCreatingIndex] = useState<number | 'append' | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftItems, setDraftItems] = useState<ItemRow[]>([emptyItem()])

  const tagEmojiMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.emoji])),
    [availableTags]
  )
  const blockMap = useMemo(
    () => Object.fromEntries(availableBlocks.map(b => [b.id, b])),
    [availableBlocks]
  )

  useEffect(() => {
    Promise.all([
      trainingBlockApi.getBlocks(),
      trainingBlockApi.getTags(),
    ]).then(([blocks, tags]) => {
      setAvailableBlocks(blocks)
      setTagCatalog(tags)
      setAvailableTags(flattenTrainingTags(tags))
    }).catch(() => {
      setError(t.createTrainingProgram.error.loadBlocksFailed)
    }).finally(() => {
      setLoadingBlocks(false)
    })
  }, [t])

  useEffect(() => {
    if (!isEdit) return
    trainingProgramApi.getProgram(Number(id)).then(program => {
      setName(program.name)
      setProgramTags(program.tags ?? [])
      setSlots(program.days.map(d => ({ kind: 'existing' as const, blockId: d.blockId })))
    }).catch(() => {
      setError(t.createTrainingProgram.error.loadFailed)
    })
  }, [id, isEdit, t])

  if (blocked) {
    return <Navigate to="/training-programs" replace />
  }

  function defaultBlockName(index: number | 'append'): string {
    const n = index === 'append' ? slots.length + 1 : index + 1
    return translate('createTrainingProgram.defaultBlockName', { n })
  }

  function cloneItems(items: ItemRow[]): ItemRow[] {
    return items.map(item => ({
      note: item.note,
      names: [...(item.names ?? [])],
    }))
  }

  function openCreate(index: number | 'append') {
    setCreatingIndex(index)
    const existing = index !== 'append' ? slots[index] : undefined
    if (existing?.kind === 'new') {
      setDraftName(existing.name)
      setDraftItems(cloneItems(existing.items))
    } else {
      setDraftName(defaultBlockName(index))
      setDraftItems([emptyItem()])
    }
    setError(null)
  }

  function confirmCreateBlock() {
    if (creatingIndex == null) return
    const blockName = draftName.trim() || defaultBlockName(creatingIndex)
    const payloadItems = itemsToApi(draftItems)
    if (payloadItems.length === 0) {
      setError(t.createTrainingProgram.error.exerciseRequired)
      return
    }
    const draft: ProgramSlot = { kind: 'new', name: blockName, items: cloneItems(draftItems) }
    setSlots(prev => {
      if (creatingIndex === 'append') return [...prev, draft]
      return prev.map((slot, i) => (i === creatingIndex ? draft : slot))
    })
    setCreatingIndex(null)
    setError(null)
  }

  function handleSelectionConfirm(blockIds: number[]) {
    if (!selectionMode) return
    if (selectionMode.type === 'add') {
      setSlots(prev => [...prev, ...blockIds.map(blockId => ({ kind: 'existing' as const, blockId }))])
    } else if (selectionMode.type === 'replace' && selectionMode.slotIndex !== undefined) {
      const idx = selectionMode.slotIndex
      setSlots(prev => prev.map((slot, i) => (i === idx ? { kind: 'existing', blockId: blockIds[0] } : slot)))
    }
    setSelectionMode(null)
  }

  function removeSlot(index: number) {
    setSlots(prev => prev.filter((_, i) => i !== index))
  }

  function moveUp(index: number) {
    setSlots(prev => {
      const next = [...prev]
      ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
      return next
    })
  }

  function moveDown(index: number) {
    setSlots(prev => {
      const next = [...prev]
      ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
      return next
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError(t.createTrainingProgram.error.nameRequired)
      return
    }
    if (slots.length === 0) {
      setError(t.createTrainingProgram.error.noBlocksSelected)
      return
    }

    setSubmitting(true)
    try {
      const blockIds: number[] = []
      for (const [index, slot] of slots.entries()) {
        if (slot.kind === 'existing') {
          blockIds.push(slot.blockId)
          continue
        }
        const payloadItems = itemsToApi(slot.items)
        if (payloadItems.length === 0) {
          setError(t.createTrainingProgram.error.exerciseRequired)
          setSubmitting(false)
          return
        }
        const created = await trainingBlockApi.createBlock({
          name: slot.name.trim() || defaultBlockName(index),
          items: payloadItems,
        })
        blockIds.push(created.id)
      }
      const request = { name: name.trim(), tags: programTags, blockIds }
      if (isEdit) {
        await trainingProgramApi.updateProgram(Number(id), request)
        captureEvent('training_program_updated', { program_id: Number(id), block_count: blockIds.length })
      } else {
        await trainingProgramApi.createProgram(request)
        captureEvent('training_program_created', { block_count: blockIds.length })
      }
      navigate('/training-programs')
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || (isEdit
        ? t.createTrainingProgram.error.updateFailed
        : t.createTrainingProgram.error.createFailed))
    } finally {
      setSubmitting(false)
    }
  }

  if (loadingBlocks) {
    return <div className="p-5 text-center opacity-60 pt-16">{t.createTrainingProgram.loading}</div>
  }

  const itemLabels = {
    itemsLabel: t.createTrainingBlock.exercisesLabel,
    addExercise: t.createTrainingBlock.addExercise,
    addToGroup: t.createTrainingBlock.addToGroup,
    notePlaceholder: t.createTrainingBlock.notePlaceholder,
    addNote: t.createTrainingBlock.addNote,
    changeNote: t.createTrainingBlock.changeNote,
    doneNote: t.createTrainingBlock.doneNote,
    exerciseNamePlaceholder: t.createTrainingBlock.exerciseNamePlaceholder,
    groupLabel: t.createTrainingBlock.groupLabel,
  }

  const inputStyle = {
    backgroundColor: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    color: theme.text.primary,
  }

  return (
    <>
      <div className="p-5 pb-24">
        <header className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: theme.text.primary }}>
            {isEdit ? t.createTrainingProgram.editTitle : t.createTrainingProgram.title}
          </h1>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Program name */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: theme.text.primary }}>
              {t.createTrainingProgram.nameLabel}
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t.createTrainingProgram.namePlaceholder}
              className="w-full rounded-xl px-3 py-3 text-sm focus:outline-none transition-colors"
              style={inputStyle}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: theme.text.primary }}>
              {t.createTrainingBlock.tagsLabel}
            </label>
            <TagSelector
              catalog={tagCatalog}
              categories={PROGRAM_TAG_CATEGORIES}
              selected={programTags}
              onChange={setProgramTags}
            />
          </div>

          {/* Block sequence */}
          <div>
            <label className="block text-sm font-semibold mb-3" style={{ color: theme.text.primary }}>
              {t.createTrainingProgram.blocksLabel}
            </label>

            <div className="flex flex-col gap-3">
              {slots.map((slot, i) => {
                const block = slot.kind === 'existing' ? blockMap[slot.blockId] : undefined
                const blockName = slot.kind === 'existing'
                  ? (block?.name ?? `Block #${slot.blockId}`)
                  : slot.name
                const items = slot.kind === 'existing' ? block?.items : itemsFromRows(slot.items)
                const tags = slot.kind === 'existing' ? block?.tags : undefined
                return (
                  <TrainingBlockCard
                    key={i}
                    name={blockName}
                    items={items}
                    tags={tags}
                    tagEmojiMap={tagEmojiMap}
                    index={i + 1}
                    headerTrailing={
                      <div className="flex items-center gap-2 shrink-0">
                        {slot.kind === 'new' && (
                          <StatusBadge status={t.createTrainingProgram.newBlockBadge} variant="info" size="sm" />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (slot.kind === 'new') {
                              openCreate(i)
                              return
                            }
                            setSelectionMode({ type: 'replace', slotIndex: i })
                          }}
                          className="text-xs px-2 py-0.5 rounded-lg"
                          style={{ backgroundColor: `${theme.buttonColor}12`, color: theme.accent.text }}
                        >
                          {t.createTrainingProgram.changeBlock}
                        </button>
                      </div>
                    }
                    footer={
                      <>
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => moveUp(i)}
                            disabled={i === 0}
                            className="p-2.5 rounded-xl disabled:opacity-20 transition-opacity"
                            style={{ color: theme.text.muted }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="18 15 12 9 6 15"/>
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => moveDown(i)}
                            disabled={i === slots.length - 1}
                            className="p-2.5 rounded-xl disabled:opacity-20 transition-opacity"
                            style={{ color: theme.text.muted }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9"/>
                            </svg>
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSlot(i)}
                          className="p-2.5 rounded-xl"
                          style={{ color: '#ef4444' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </>
                    }
                  />
                )
              })}
            </div>

            <div className="flex flex-col gap-2 mt-2">
              <DashedAddButton
                onClick={() => setSelectionMode({ type: 'add' })}
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  </svg>
                }
              >
                {t.createTrainingProgram.pickSavedBlock}
              </DashedAddButton>
              <DashedAddButton onClick={() => openCreate('append')}>
                {t.createTrainingProgram.createNewBlock}
              </DashedAddButton>
            </div>
          </div>

          {error && (
            <div className="rounded-xl p-4 bg-red-500/10 border border-red-500/25">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
            {submitting
              ? t.createTrainingProgram.saving
              : isEdit ? t.createTrainingProgram.submitEdit : t.createTrainingProgram.submit}
          </Button>

          {!isEdit && (
            <>
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 border-t" style={{ borderColor: theme.cardBorder }} />
                <span className="text-xs" style={{ color: theme.text.muted }}>{t.createTrainingBlock.orDivider}</span>
                <div className="flex-1 border-t" style={{ borderColor: theme.cardBorder }} />
              </div>

              <ExperimentalTextImportOption
                title={t.createTrainingProgram.createFromText}
                description={t.createTrainingProgram.createFromTextHint}
                onClick={() => navigate('/training-programs/create-from-text')}
              />
            </>
          )}
        </form>
      </div>

      {/* Full-screen block selection overlay */}
      {selectionMode !== null && (
        <BlockSelectionPage
          blocks={availableBlocks}
          availableTags={availableTags}
          tagEmojiMap={tagEmojiMap}
          mode={selectionMode}
          currentSlotBlockId={
            selectionMode.type === 'replace' && selectionMode.slotIndex !== undefined
              ? (() => {
                  const slot = slots[selectionMode.slotIndex]
                  return slot?.kind === 'existing' ? slot.blockId : undefined
                })()
              : undefined
          }
          onConfirm={handleSelectionConfirm}
          onClose={() => setSelectionMode(null)}
          onCreateNew={() => {
            const index = selectionMode.type === 'replace' && selectionMode.slotIndex !== undefined
              ? selectionMode.slotIndex
              : 'append'
            setSelectionMode(null)
            openCreate(index)
          }}
        />
      )}

      {creatingIndex != null && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: theme.bgColor }}>
          <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
            <button type="button" onClick={() => { setCreatingIndex(null); setError(null) }} className="p-2 -ml-2 rounded-xl" style={{ color: theme.text.primary }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
              {t.createTrainingProgram.createNewBlock}
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
            <input
              type="text"
              value={draftName}
              onChange={e => setDraftName(e.target.value)}
              placeholder={t.createTrainingProgram.defaultBlockName.replace('{{n}}', '1')}
              className="w-full rounded-xl px-4 py-3 text-base outline-none"
              style={{ backgroundColor: theme.inputBg, color: theme.text.primary, border: `1px solid ${theme.inputBorder}` }}
            />
            <BlockItemsEditor
              items={draftItems}
              onChange={setDraftItems}
              textColor={theme.text.primary}
              bgColor={theme.bgColor}
              labels={itemLabels}
            />
            {error && (
              <div className="rounded-xl p-3 bg-red-500/10 border border-red-500/25">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </div>
          <div className="px-4 pb-8 pt-3 shrink-0" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
            <Button type="button" variant="primary" size="lg" fullWidth onClick={confirmCreateBlock}>
              {t.createTrainingProgram.addBlock}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
